import { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { getRSVPInviteBySlug, getGuestById, updateGuestRSVP } from '../../services/rsvpInviteService'
import { getFormQuestions, formatQuestionLabel } from '../../constants/rsvpFormQuestions'
import { showAlert } from '../../services/dialogService'
import TextareaWithCounter from '../../components/TextareaWithCounter/TextareaWithCounter'
import './RSVPForm.css'

function RSVPForm() {
  const { slug, guestId } = useParams()
  const [invite, setInvite] = useState(null)
  const [guest, setGuest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const [formData, setFormData] = useState({
    attending: null,
    dietary_requirements: '',
    allergies: '',
    notes: '',
    custom: {},
  })

  const questions = useMemo(() => getFormQuestions(invite), [invite])
  const hasAttendance = useMemo(() => questions.some((q) => q.type === 'attendance'), [questions])

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        const [inviteData, guestData] = await Promise.all([
          getRSVPInviteBySlug(slug),
          guestId ? getGuestById(guestId) : null,
        ])

        if (!inviteData) {
          setLoading(false)
          return
        }

        setInvite(inviteData)
        setGuest(guestData)

        if (guestData) {
          const dietaryQuestion = inviteData.form_questions && Array.isArray(inviteData.form_questions)
            ? inviteData.form_questions.find((q) => q.type === 'dietary')
            : null
          const dietaryOptions = dietaryQuestion?.options || ['None', 'Vegetarian', 'Vegan', 'Celiac', 'Kosher', 'Pescatarian']
          const dietaryMultiple = !!dietaryQuestion?.multiple
          let dietaryValue
          if (dietaryMultiple && guestData.dietary_requirements) {
            const values = guestData.dietary_requirements.split(/\s*,\s*/).filter(Boolean)
            const indices = []
            values.forEach((v) => {
              const i = dietaryOptions.findIndex((opt, idx) => (opt || '').toString() === (v || '').toString() && !indices.includes(idx))
              if (i !== -1) indices.push(i)
            })
            dietaryValue = indices
          } else if (dietaryMultiple) {
            dietaryValue = []
          } else {
            const dietaryOption = dietaryOptions.find(
              (opt) => (opt && opt.toLowerCase()) === (guestData.dietary_requirements || '').toLowerCase()
            ) || guestData.dietary_requirements
            dietaryValue = dietaryOption || ''
          }

          const customRaw = guestData.custom_rsvp_answers && typeof guestData.custom_rsvp_answers === 'object' ? guestData.custom_rsvp_answers : {}
          const questionsList = inviteData.form_questions && Array.isArray(inviteData.form_questions) ? inviteData.form_questions : []
          const custom = {}
          Object.keys(customRaw).forEach((k) => {
            const q = questionsList.find((qu) => qu.id === k)
            const raw = customRaw[k]
            if (q?.type === 'custom_choice' && q.multiple && raw != null) {
              const values = Array.isArray(raw) ? raw.map(String) : [String(raw)]
              const opts = q.options || []
              const indices = []
              values.forEach((v) => {
                const i = opts.findIndex((opt, idx) => (opt || '').toString() === (v || '').toString() && !indices.includes(idx))
                if (i !== -1) indices.push(i)
              })
              custom[k] = indices
            } else {
              custom[k] = raw != null ? raw : ''
            }
          })

          setFormData({
            attending: guestData.rsvp_status === 'confirmed' ? true : guestData.rsvp_status === 'declined' ? false : null,
            dietary_requirements: dietaryValue,
            allergies: guestData.allergies || '',
            notes: guestData.notes || '',
            custom,
          })
        }
      } catch (error) {
        console.error('Error loading data:', error)
      } finally {
        setLoading(false)
      }
    }

    if (slug) {
      loadData()
    }
  }, [slug, guestId])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleCustomChange = (questionId, value) => {
    setFormData((prev) => ({
      ...prev,
      custom: { ...prev.custom, [questionId]: value },
    }))
  }

  const handleAttendingChange = (value) => {
    setFormData((prev) => ({ ...prev, attending: value }))
  }

  const handleDietaryChange = (value) => {
    setFormData((prev) => ({ ...prev, dietary_requirements: value }))
  }

  const handleDietaryMultipleToggle = (optionIndex) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.dietary_requirements) ? prev.dietary_requirements : []
      const has = current.includes(optionIndex)
      const next = has ? current.filter((i) => i !== optionIndex) : [...current, optionIndex].sort((a, b) => a - b)
      return { ...prev, dietary_requirements: next }
    })
  }

  const handleCustomChoiceMultipleToggle = (questionId, optionIndex) => {
    setFormData((prev) => {
      const current = prev.custom[questionId]
      const arr = Array.isArray(current) ? current : []
      const has = arr.includes(optionIndex)
      const next = has ? arr.filter((i) => i !== optionIndex) : [...arr, optionIndex].sort((a, b) => a - b)
      return { ...prev, custom: { ...prev.custom, [questionId]: next } }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!guest) return
    if (hasAttendance && formData.attending === null) {
      await showAlert('Please select whether you will be able to attend.')
      return
    }

    try {
      setSubmitting(true)
      const dietaryVal = formData.dietary_requirements
      const dietaryQuestion = questions.find((qu) => qu.type === 'dietary')
      const dietaryOptions = dietaryQuestion?.options || []
      const dietaryPayload = Array.isArray(dietaryVal)
        ? (dietaryVal.length ? dietaryVal.map((i) => dietaryOptions[i]).filter(Boolean).join(', ') : null)
        : (dietaryVal || null)
      const customForPayload = {}
      Object.keys(formData.custom || {}).forEach((k) => {
        const v = formData.custom[k]
        const qu = questions.find((q) => q.id === k)
        if (Array.isArray(v) && qu?.type === 'custom_choice' && qu.options) {
          customForPayload[k] = v.map((i) => qu.options[i]).filter((x) => x != null)
        } else {
          customForPayload[k] = v
        }
      })
      const payload = {
        attending: formData.attending,
        dietary_requirements: dietaryPayload,
        allergies: formData.allergies || null,
        notes: formData.notes || null,
        custom_rsvp_answers: Object.keys(customForPayload).length ? customForPayload : undefined,
      }
      await updateGuestRSVP(guest.id, payload)
      setSubmitted(true)
    } catch (error) {
      console.error('Error submitting RSVP:', error)
      await showAlert('Error submitting RSVP. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
    } catch {
      return dateString
    }
  }

  if (loading) {
    return (
      <div className="rsvp-form-page">
        <div className="rsvp-form-container">
          <div className="rsvp-loading">Loading...</div>
        </div>
      </div>
    )
  }

  if (!invite || !guest) {
    return (
      <div className="rsvp-form-page">
        <div className="rsvp-form-container">
          <div className="rsvp-error">Invitation or guest not found</div>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="rsvp-form-page">
        <div className="rsvp-form-container">
          <div className="rsvp-success">
            <div className="rsvp-success-icon">✓</div>
            <h2>Thank you!</h2>
            <p>Your RSVP has been submitted successfully.</p>
            <p>We look forward to celebrating with you!</p>
          </div>
        </div>
      </div>
    )
  }

  const coupleNames = invite.partner2_name ? `${invite.partner1_name} & ${invite.partner2_name}` : invite.partner1_name

  return (
    <div className="rsvp-form-page">
      <div className="rsvp-form-container">
        <div className="rsvp-form-header">
          <div className="rsvp-form-brand">{coupleNames}</div>
        </div>

        <div className="rsvp-form-event-info">
          {invite.wedding_venue && <div className="rsvp-form-venue">{invite.wedding_venue}</div>}
          {invite.wedding_date && <div className="rsvp-form-date">{formatDate(invite.wedding_date)}</div>}
        </div>

        <div className="rsvp-form-guest-info">
          <p>Responding for {guest.first_name} {guest.last_name}</p>
        </div>

        <form onSubmit={handleSubmit} className="rsvp-form">
          {questions.map((q, index) => {
            const num = index + 1
            const label = q.type === 'attendance' ? formatQuestionLabel(q.label, invite.wedding_date) : q.label

            if (q.type === 'attendance') {
              return (
                <div key={q.id} className="rsvp-form-question">
                  <label className="rsvp-question-label">{num}. {label}</label>
                  <div className="rsvp-option-buttons">
                    {(q.options || ["Yes, I wouldn't miss it :)", "No, I won't be able to make it :("]).map((opt, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`rsvp-option-btn ${formData.attending === (i === 0) ? 'selected' : ''}`}
                        onClick={() => handleAttendingChange(i === 0)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              )
            }

            if (q.type === 'dietary') {
              const options = q.options || ['None', 'Vegetarian', 'Vegan', 'Celiac', 'Kosher', 'Pescatarian']
              const multiple = !!q.multiple
              const selectedIndices = multiple && Array.isArray(formData.dietary_requirements)
                ? formData.dietary_requirements.filter((i) => typeof i === 'number' && i >= 0 && i < options.length)
                : []
              if (multiple) {
                return (
                  <div key={q.id} className="rsvp-form-question">
                    <label className="rsvp-question-label">{num}. {label}</label>
                    <div className="rsvp-option-buttons">
                      {options.map((option, idx) => {
                        const isSelected = selectedIndices.includes(idx)
                        return (
                          <button
                            key={idx}
                            type="button"
                            className={`rsvp-option-btn ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleDietaryMultipleToggle(idx)}
                          >
                            {option}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              }
              return (
                <div key={q.id} className="rsvp-form-question">
                  <label className="rsvp-question-label">{num}. {label}</label>
                  <div className="rsvp-option-buttons">
                    {options.map((option) => {
                      const value = option.toLowerCase ? option.toLowerCase() : option
                      const isSelected = (formData.dietary_requirements || '').toLowerCase() === value || formData.dietary_requirements === option
                      return (
                        <button
                          key={option}
                          type="button"
                          className={`rsvp-option-btn ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleDietaryChange(option)}
                        >
                          {option}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            }

            if (q.type === 'allergies') {
              return (
                <div key={q.id} className="rsvp-form-question">
                  <label className="rsvp-question-label">{num}. {label}</label>
                  <input
                    type="text"
                    name="allergies"
                    value={formData.allergies}
                    onChange={handleInputChange}
                    placeholder={q.placeholder || 'Please specify'}
                    className="rsvp-text-input"
                  />
                </div>
              )
            }

            if (q.type === 'notes') {
              return (
                <div key={q.id} className="rsvp-form-question">
                  <label className="rsvp-question-label">{num}. {label}</label>
                  <TextareaWithCounter
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder={q.placeholder || 'Please let us know...'}
                    className="rsvp-textarea-input"
                    rows={4}
                    maxLength={500}
                  />
                </div>
              )
            }

            if (q.type === 'custom_choice' && q.options && q.options.length) {
              const val = formData.custom[q.id]
              const multiple = !!q.multiple
              const selectedIndices = multiple && Array.isArray(val) ? val.filter((i) => typeof i === 'number' && i >= 0 && i < q.options.length) : []
              if (multiple) {
                return (
                  <div key={q.id} className="rsvp-form-question">
                    <label className="rsvp-question-label">{num}. {label}</label>
                    <div className="rsvp-option-buttons">
                      {q.options.map((option, idx) => {
                        const isSelected = selectedIndices.includes(idx)
                        return (
                          <button
                            key={idx}
                            type="button"
                            className={`rsvp-option-btn ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleCustomChoiceMultipleToggle(q.id, idx)}
                          >
                            {option}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              }
              return (
                <div key={q.id} className="rsvp-form-question">
                  <label className="rsvp-question-label">{num}. {label}</label>
                  <div className="rsvp-option-buttons">
                    {q.options.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={`rsvp-option-btn ${val === option ? 'selected' : ''}`}
                        onClick={() => handleCustomChange(q.id, option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              )
            }

            if (q.type === 'custom_textarea') {
              return (
                <div key={q.id} className="rsvp-form-question">
                  <label className="rsvp-question-label">{num}. {label}</label>
                  <TextareaWithCounter
                    value={formData.custom[q.id] || ''}
                    onChange={(e) => handleCustomChange(q.id, e.target.value)}
                    placeholder={q.placeholder || ''}
                    className="rsvp-textarea-input"
                    rows={3}
                    maxLength={500}
                  />
                </div>
              )
            }

            if (q.type === 'custom_text' || q.type === 'custom_choice') {
              return (
                <div key={q.id} className="rsvp-form-question">
                  <label className="rsvp-question-label">{num}. {label}</label>
                  <input
                    type="text"
                    value={formData.custom[q.id] || ''}
                    onChange={(e) => handleCustomChange(q.id, e.target.value)}
                    placeholder={q.placeholder || ''}
                    className="rsvp-text-input"
                  />
                </div>
              )
            }

            return null
          })}

          <button
            type="submit"
            className="rsvp-submit-btn"
            disabled={submitting || (hasAttendance && formData.attending === null)}
          >
            {submitting ? 'Submitting...' : 'Submit RSVP'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default RSVPForm
